import React, { useEffect, useMemo, useRef, useState } from 'react';
import { normalizeLabel, MAX_LABEL_LENGTH } from './projectLabel';

/**
 * What the picker hands back: a client (and maybe a task), or a free-text label.
 *
 * - `clientId` is the chosen client's; `taskId` is present only when a task was chosen (a
 *   client chosen alone leaves it out, and the backend clears a task that belongs to the
 *   previous client).
 * - `label` is typed text to store as the entry's `service` label. It carries no client or
 *   task and changes neither.
 *
 * @typedef {{clientId: string, clientName: string, taskId?: string, taskName?: string}|{label: string}} ProjectChoice
 */

/**
 * A client-then-task chooser: a search box with a listbox under it.
 *
 * Stage one lists the caller's clients. Choosing one moves to stage two, its open tasks,
 * where the first option is the client on its own. There is no "no project" option and no
 * "no task" option for the entry's current client: the backend reads a null as "keep the
 * current value", so neither could be expressed until it names an explicit clear. The same
 * goes for the free-text label: choosing one replaces it, and there is no "clear label".
 *
 * On the client list, typed text that is not an exact (case-insensitive) match of a client
 * name or of the entry's current client, task or label adds a last option, Add “text”
 * (Replace “current” with “text” when the entry already has a label, which may be a legacy
 * category such as "web"). It stores the normalised text as the entry's `service` label
 * (1 to 60 characters, whitespace collapsed). It is offered only once the client list has
 * loaded, and a lone one is not pre-highlighted: it needs an arrow press before Enter.
 *
 * ARIA: the input is the `combobox`, it owns the `listbox` (`aria-controls`) and points at
 * the highlighted option with `aria-activedescendant`. Arrow keys move the highlight,
 * Enter chooses, Esc closes without choosing.
 *
 * @param {object} props
 * @param {{client_id?: string|null, task_id?: string|null}} props.entry The entry's current project.
 * @param {() => Promise<{id: string, name: string}[]>} props.loadClients
 * @param {(clientId: string) => Promise<{id: string, title: string}[]>} props.loadTasks
 * @param {(choice: ProjectChoice) => void} props.onChoose
 * @param {() => void} props.onClose Esc.
 * @param {string} props.idPrefix Makes the listbox and option ids unique per row.
 */
export default function ProjectPicker({ entry, loadClients, loadTasks, onChoose, onClose, idPrefix }) {
  const [stage, setStage] = useState('clients');
  const [client, setClient] = useState(null);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [active, setActive] = useState(0);
  // Whether the user has moved the highlight with the arrow keys. A lone "Add" row is not
  // pre-highlighted until they have, so Enter alone cannot store a label by accident.
  const [moved, setMoved] = useState(false);
  const inputRef = useRef(null);
  const attempt = useRef(0);

  useEffect(() => {
    inputRef.current?.focus();
  }, [stage]);

  // Load the list for the stage. A ticket drops the answer of a request that has since
  // been replaced (back to the clients while the tasks were still loading).
  useEffect(() => {
    const ticket = ++attempt.current;
    setStatus('loading');
    setItems([]);
    const request = stage === 'clients' ? loadClients() : loadTasks(client.id);
    request
      .then((list) => {
        if (ticket !== attempt.current) return;
        setItems(Array.isArray(list) ? list : []);
        setStatus('ready');
      })
      .catch(() => {
        if (ticket !== attempt.current) return;
        setStatus('error');
      });
  }, [stage, client, loadClients, loadTasks]);

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (text) => !needle || String(text || '').toLowerCase().includes(needle);

    if (stage === 'clients') {
      const list = items
        .filter((c) => matches(c.name))
        .map((c) => ({
          key: `client-${c.id}`,
          label: c.name,
          selected: c.id === entry?.client_id,
          kind: 'client',
          client: c,
        }));
      // Free text that names nothing listed is offered as a label. An exact match of a
      // client, or of what the entry already shows, is not: the list or the row has it.
      const typed = normalizeLabel(query);
      const known = [...items.map((c) => c.name), entry?.client, entry?.task, entry?.service]
        .filter(Boolean)
        .map((name) => normalizeLabel(name).value.toLowerCase());
      // Offered only once the roster has loaded: before that, a client's name typed fast
      // would be stored as a label; and not on a failed load, where the roster is unknown
      // (the user sees "Couldn't load" instead).
      if (status === 'ready' && !typed.empty && !typed.tooLong && !known.includes(typed.value.toLowerCase())) {
        // A label replaces the current one, which may be a legacy category such as "web".
        const current = normalizeLabel(entry?.service).value;
        const label = current
          ? `Replace “${current}” with “${typed.value}”`
          : `Add “${typed.value}”`;
        list.push({ key: 'free', label, selected: false, kind: 'free', text: typed.value });
      }
      return list;
    }

    const list = [];
    // The client alone is a change only when it is not already the entry's client.
    if (client && client.id !== entry?.client_id && matches(client.name)) {
      list.push({ key: `only-${client.id}`, label: `${client.name}, no task`, selected: false, kind: 'client-only' });
    }
    for (const t of items) {
      if (matches(t.title)) {
        list.push({ key: `task-${t.id}`, label: t.title, selected: t.id === entry?.task_id, kind: 'task', task: t });
      }
    }
    return list;
  }, [stage, items, query, client, entry, status]);

  const loneFree = options.length === 1 && options[0].kind === 'free';
  const activeIndex = loneFree && !moved ? -1 : active;

  // Keep the highlight on a real option when the list shrinks.
  useEffect(() => {
    setActive((a) => (options.length === 0 ? 0 : Math.min(a, options.length - 1)));
  }, [options.length]);

  const optionId = (i) => `${idPrefix}-opt-${i}`;
  const listId = `${idPrefix}-list`;

  /** Highlights `next` and records that the user, not the list, chose it. */
  const highlight = (next) => {
    setMoved(true);
    setActive(next);
  };

  const choose = (option) => {
    if (!option) return;
    if (option.kind === 'client') {
      setClient(option.client);
      setStage('tasks');
      setQuery('');
      setActive(0);
    } else if (option.kind === 'free') {
      onChoose({ label: option.text });
    } else if (option.kind === 'client-only') {
      onChoose({ clientId: client.id, clientName: client.name });
    } else {
      onChoose({ clientId: client.id, clientName: client.name, taskId: option.task.id, taskName: option.task.title });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlight(options.length ? (activeIndex + 1) % options.length : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlight(options.length ? (Math.max(activeIndex, 0) - 1 + options.length) % options.length : 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(options[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const back = () => {
    setStage('clients');
    setClient(null);
    setQuery('');
    setActive(0);
  };

  const emptyText = status === 'error'
    ? "Couldn't load. Close and try again."
    : status === 'loading'
      ? 'Loading…'
      : stage === 'clients'
        ? 'No matching clients'
        : 'No matching tasks';

  return (
    <div data-testid="project-picker" className="w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-2">
      {stage === 'tasks' && (
        <button
          type="button"
          onClick={back}
          data-testid="project-back"
          className="mb-1 flex w-full items-center gap-1 rounded-md px-1.5 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <span aria-hidden="true">‹</span>
          <span className="truncate">{client?.name}</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={stage === 'clients' ? 'Search clients' : 'Search tasks'}
        aria-expanded="true"
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={options.length && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        placeholder={stage === 'clients' ? 'Search clients' : 'Search tasks'}
        autoComplete="off"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setActive(0); setMoved(false); }}
        onKeyDown={onKeyDown}
        data-testid="project-search"
        className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-transparent px-2 py-1 text-sm focus:border-[#32B1FF] focus:outline-none focus:ring-1 focus:ring-[#32B1FF]"
      />
      <ul
        id={listId}
        role="listbox"
        aria-label={stage === 'clients' ? 'Clients' : 'Tasks'}
        className="mt-1 max-h-56 overflow-auto"
        data-testid="project-list"
      >
        {options.map((o, i) => (
          <li
            key={o.key}
            id={optionId(i)}
            role="option"
            aria-selected={o.selected}
            data-testid={o.kind === 'free' ? 'project-add-free' : 'project-option'}
            data-active={i === activeIndex ? 'true' : undefined}
            onMouseEnter={() => highlight(i)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => choose(o)}
            className={`flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200 ${i === activeIndex ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
          >
            <span className="truncate">{o.label}</span>
            {o.selected && <span aria-hidden="true" className="text-[#32B1FF]">✓</span>}
            {o.kind === 'client' && !o.selected && <span aria-hidden="true" className="text-gray-400">›</span>}
          </li>
        ))}
      </ul>
      {stage === 'clients' && normalizeLabel(query).tooLong && (
        <p role="status" className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400" data-testid="project-free-hint">
          Use {MAX_LABEL_LENGTH} characters or fewer
        </p>
      )}
      {options.length === 0 && (
        <p role="status" className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400" data-testid="project-empty">
          {emptyText}
        </p>
      )}
    </div>
  );
}
